/**
 * Carousel Component
 * --------------------------------------------------------------------
 * @summary carousel, uses swiper, dots are optional, and arrows are optional
 *
 * See https://swiperjs.com/swiper-api#parameters for more carousel options
 *
 * @usage
 * 
 *     {%- capture carousel_options -%}
 *       {
 *         "slidesPerView": "auto",
 *         "loop": false,
 *         ...
 *       }
 *     {%- endcapture -%}
 *      <s-carousel data-options='{{carousel_options}}' data-init="{{breakpoint}}">
 *        <div id="{{custom_id}}" class="swiper" js-carousel>
 *          <div class="swiper-wrapper">
 *            <div class="swiper-slide" js-carousel-slide></div>
 *            ...
 *          </div>
 *          <div class="swiper-button-prev {{prev_button_class}}"></div>
 *          <div class="swiper-button-next {{next_button_class}}"></div>
 *          <div class="swiper-pagination {{pagination_class}}"></div>
 *        </div>
 *      </s-carousel>
 *
 * The following parameters are attributes that the <s-carousel> element takes
 * @param data-options - object containing swiper options
 *                       ex.{"slidesPerView":"auto", "loop":false}
 *                       only needed if modifying or extending on default options
 *                       be careful with syntax here you have to use single quote on the outside,
 *                       and double quotes in the capture so that the JSON.parse() doesn't error.
 *                       DEFAULT OPTIONS:
 *                          loop: true,
 *                          slidesPerView: 'auto',
 *                          grabCursor: true,
 *                          centeredSlides: false,
 *                          on: {init: this._onReady},
 * 
 * @param data-init - use to initialize or destroy carousel at certain widths.
 *                    pass in variable from theme.config.mediaQueries (/global/config.js)
 *                    
 *                    enquire match/unmatch is pretty specific, so follow the rules below:
 *                    if mobile = destroyed, higher width = initialized, use a max-width variable
 *                    if mobile = intialized, higher width = destroyed, use a min-width variable
 * 
 *                    if breakpoint does not exist, feel free to add to config.js
 *
 * The following are selectors that the <s-carousel> searches for
 * [js-carousel] - required, this contains the slides and the arrows.
 * id - required, should be same element as [js-carousel]
 * .swiper - required class
 * .swiper-wrapper - required class
 * [js-carousel-slide] - required, each slide element needs this (differentiates from the arrows)
 * .swiper-slide - required class
 * .swiper-pagination - optional, custom {{pagination_class}} required if using pagination
 * .swiper-button-prev - optional, custom {{prev_button_class}} required if using navigation
 * .swiper-button-next - optional, custom {{next_button_class}} required if using navigation
 */

 class Carousel extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      carousel: '[js-carousel]',
      playBtn: '[js-play-btn]',
      pauseBtn: '[js-pause-btn]',
      slideImages: '[js-slide-image]'
    }

    this.options = {
      loop: true,
      slidesPerView: 'auto',
      grabCursor: true,
      centeredSlides: false,
      navigation: false,
      pagination: false,
      speed: 1000,
    }
  }

  connectedCallback() {
    const extendedOptions = this.dataset.options ? JSON.parse(this.dataset.options) : {}
    this.options = Object.assign(this.options, extendedOptions)
    if (typeof this.dataset.connectedCarousel != 'undefined') {
      let connectedCarousel = document.querySelector(`#${this.dataset.connectedCarousel}`);
      this.options = Object.assign(this.options, {thumbs: {swiper: connectedCarousel.swiper}})
    }
    this._initCarousel();

    this.playBtn = this.querySelector(this.selectors.playBtn);
    this.pauseBtn = this.querySelector(this.selectors.pauseBtn);
    this.slideImages = this.querySelectorAll(this.selectors.slideImages);

    if (this.playBtn && this.pauseBtn) {
      this.pauseBtn.addEventListener('click', this._handlePauseClick);
      this.playBtn.addEventListener('click', this._handlePlayClick);
    }

    if (this.slideImages && this.carousel) {
      // this.carousel.on('slideChange', this._handleSlideChange)
    }
  }

  // Initialize carousel
  _initCarousel() {
    let id = `#${this.querySelector(this.selectors.carousel).id}`;
    if (typeof this.dataset.init !== 'undefined') {
      //do nothing initially, init at larger points
      enquire.register(theme.config.mediaQueries[this.dataset.init],{
        match: function(){
          this.carousel = new Swiper(id, this.options)
        }.bind(this),
        unmatch: function(){
          if (this.carousel.el.classList.contains('swiper-initialized')) {
            this.carousel.destroy(true, true);
          }
        }.bind(this)
      })
    }else{
      this.carousel = new Swiper(id, this.options)
    }
    if (this.dataset.connectedCarousel != undefined && this.carousel != null) {
      let connectedCarousel = document.querySelector(`#${this.dataset.connectedCarousel}`);
      let thisCarousel = this.carousel
      thisCarousel.on("slideChangeTransitionStart", function () {
        connectedCarousel.swiper.slideTo(thisCarousel.realIndex, 300, false);
        connectedCarousel.swiper.slides.forEach(slide => {
          slide.classList.remove('swiper-slide-active', 'swiper-slide-thumb-active');
        })
        if (connectedCarousel.swiper.slides.length) {
          connectedCarousel.swiper.slides[thisCarousel.realIndex].classList.add('swiper-slide-active', 'swiper-slide-thumb-active');
        }
      });
      enquire.register(theme.config.mediaQueries.desktop,{
        match: function(){
          connectedCarousel.swiper.on("transitionStart", function () {
            thisCarousel.slideTo(connectedCarousel.swiper.activeIndex, 300, false);
          });
        }.bind(this),
        unmatch: function(){
        }.bind(this)
      });
    }
  }

  _handlePauseClick = (e) => {
    this.playBtn.classList.remove('hidden')
    this.pauseBtn.classList.add('hidden')
    this.carousel.autoplay.pause()
  }

  _handlePlayClick = (e) => {
    this.playBtn.classList.add('hidden')
    this.pauseBtn.classList.remove('hidden')
    this.carousel.autoplay.start()
  }

  _handleSlideChange = (e) => {
    for (let i = 0; i < this.slideImages.length; i++ ) {
      this.slideImages[i].classList.contains('grayscale') ? null : this.slideImages[i].classList.add('grayscale')
      this.slideImages[i].classList.contains('opacity-50') ? null : this.slideImages[i].classList.add('opacity-50')
      const slideIndex = this.slideImages[i].getAttribute('js-slide-index')
      if (this.carousel.activeIndex == slideIndex ) {
        this.slideImages[i].classList.remove('grayscale')
        this.slideImages[i].classList.remove('opacity-50')
      }
    }
  }
}

customElements.define("s-carousel", Carousel);